import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { UserService } from '../services/user.service';
import { PosthogAnalyticsService } from './posthog-analytics.service';

type PageContext = {
  page_view_id: string;
  route_pattern: string;
  page_name: string;
  gp_id?: string;
  vehicle_id?: string;
  fund_id?: string;
  user_id?: string;
  user_email?: string;
  session_id: string;
  started_at_ms: number;
};

const SESSION_ID_KEY = 'gp_portal_session_id';

@Injectable({ providedIn: 'root' })
export class PageAnalyticsService implements OnDestroy {
  private started = false;
  private routerSub?: Subscription;
  private userSub?: Subscription;

  private current: PageContext | null = null;
  private activeMs = 0;
  private activeStartMs: number | null = null;

  private visibilityHandler = () => this.syncActiveState();
  private focusHandler = () => this.syncActiveState();
  private blurHandler = () => this.syncActiveState();
  private beforeUnloadHandler = () => this.flushCurrent(Date.now());

  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly posthog: PosthogAnalyticsService,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;

    // Identify the user once we have any stable id.
    this.userSub = this.userService.userDataObservable$.subscribe(user => {
      const id = (user as any)?.id ?? this.userService.getUserConfigObject().id;
      if (!id) return;
      const email = (user as any)?.email ?? (user as any)?.userName ?? undefined;
      const name =
        (user as any)?.firstName || (user as any)?.lastName
          ? `${(user as any)?.firstName ?? ''} ${(user as any)?.lastName ?? ''}`.trim()
          : this.userService.getUserConfigObject().name;
      this.posthog.identify(String(id), {
        user_id: id,
        user_email: email,
        name,
      });
    });

    // Track navigation.
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.onNavigation());

    // Track active/visible time.
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
      window.addEventListener('focus', this.focusHandler);
      window.addEventListener('blur', this.blurHandler);
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    // Initial page.
    this.onNavigation();
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.userSub?.unsubscribe();
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      window.removeEventListener('focus', this.focusHandler);
      window.removeEventListener('blur', this.blurHandler);
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  private onNavigation(): void {
    const now = Date.now();
    this.flushCurrent(now);

    const snapshot = this.router.routerState.snapshot.root;
    const url = this.router.url || '';
    const route_pattern = this.routePatternFromSnapshot(snapshot);
    const ids = this.idsFromSnapshot(snapshot);
    const idsFromUrl = this.idsFromUrl(url);
    const gp_id = ids.gp_id ?? idsFromUrl.gp_id;
    const vehicle_id = ids.vehicle_id ?? idsFromUrl.vehicle_id;

    const page_name = this.pageNameFromUrl(url, route_pattern);
    const session_id = this.getOrCreateSessionId();

    const userCfg = this.userService.getUserConfigObject();
    const user_id = userCfg?.id != null ? String(userCfg.id) : undefined;
    const user_email =
      (this.userService as any)?.userData?.value?.email ??
      (this.userService as any)?.userDetails?.email ??
      undefined;

    const page_view_id = this.uuid();

    this.current = {
      page_view_id,
      route_pattern,
      page_name,
      gp_id: gp_id ?? undefined,
      vehicle_id: vehicle_id ?? undefined,
      // fund_id is optional and should only be attached when reliably known
      session_id,
      user_id,
      user_email,
      started_at_ms: now,
    };

    this.activeMs = 0;
    this.activeStartMs = null;
    this.syncActiveState(now);

    this.posthog.capture('page_viewed', this.baseProps(this.current));
  }

  private flushCurrent(now = Date.now()): void {
    if (!this.current) return;
    this.syncActiveState(now);

    const duration_seconds = Math.max(
      0,
      Math.round((now - this.current.started_at_ms) / 1000),
    );
    const active_duration_seconds = Math.max(
      0,
      Math.round(this.activeMs / 1000),
    );

    this.posthog.capture('page_left', {
      ...this.baseProps(this.current),
      duration_seconds,
      active_duration_seconds,
    });

    this.current = null;
    this.activeMs = 0;
    this.activeStartMs = null;
  }

  private syncActiveState(now = Date.now()): void {
    if (!this.current) return;
    if (typeof document === 'undefined') return;

    const isVisible = document.visibilityState === 'visible';
    const isFocused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    const isActive = isVisible && isFocused;

    if (isActive) {
      if (this.activeStartMs == null) {
        this.activeStartMs = now;
      }
      return;
    }

    if (this.activeStartMs != null) {
      this.activeMs += Math.max(0, now - this.activeStartMs);
      this.activeStartMs = null;
    }
  }

  private baseProps(ctx: PageContext): Record<string, unknown> {
    return {
      page_view_id: ctx.page_view_id,
      route_pattern: ctx.route_pattern,
      page_name: ctx.page_name,
      gp_id: ctx.gp_id,
      vehicle_id: ctx.vehicle_id,
      fund_id: ctx.fund_id,
      user_id: ctx.user_id,
      user_email: ctx.user_email,
      session_id: ctx.session_id,
    };
  }

  private getOrCreateSessionId(): string {
    try {
      if (typeof sessionStorage === 'undefined') return this.uuid();
      const existing = sessionStorage.getItem(SESSION_ID_KEY);
      if (existing) return existing;
      const created = this.uuid();
      sessionStorage.setItem(SESSION_ID_KEY, created);
      return created;
    } catch {
      return this.uuid();
    }
  }

  private uuid(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }

  private idsFromSnapshot(snapshot: ActivatedRouteSnapshot): {
    gp_id?: string;
    vehicle_id?: string;
  } {
    let gp_id: string | undefined;
    let vehicle_id: string | undefined;
    let node: ActivatedRouteSnapshot | null = snapshot;

    while (node) {
      gp_id = gp_id ?? node.paramMap.get('gpId') ?? undefined;
      vehicle_id = vehicle_id ?? node.paramMap.get('vehicleId') ?? undefined;
      node = node.firstChild;
    }

    return { gp_id, vehicle_id };
  }

  private idsFromUrl(url: string): { gp_id?: string; vehicle_id?: string } {
    const match = (url || '').match(/\/gp\/([^/?#]+)\/vehicle\/([^/?#]+)/i);
    if (!match) return {};
    return { gp_id: match[1], vehicle_id: match[2] };
  }

  private routePatternFromSnapshot(snapshot: ActivatedRouteSnapshot): string {
    const segments: string[] = [];

    let node: ActivatedRouteSnapshot | null = snapshot;
    while (node) {
      const path: string | undefined = node.routeConfig?.path;
      if (path && path !== '**') {
        const parts = path.split('/').filter(Boolean);
        segments.push(...parts);
      }
      node = node.firstChild;
    }

    const pattern = '/' + segments.join('/');
    return pattern === '/' ? this.router.url || '/' : pattern;
  }

  private pageNameFromUrl(url: string, routePattern: string): string {
    const lower = (routePattern || url || '').toLowerCase();
    if (lower.includes('/management')) return 'Management';
    if (lower.includes('/onboarding')) return 'Onboarding';
    if (lower.includes('/investors')) return 'Investors';
    if (lower.includes('/chat')) return 'Chat';
    if (lower.includes('/chats')) return 'Chats';
    if (lower.includes('/team')) return 'Team';
    if (lower.includes('/faq')) return 'FAQ';
    if (lower.includes('/documents')) return 'Documents';
    if (lower.includes('/transactions')) return 'Transactions';
    if (lower.includes('/portfolio')) return 'Portfolio';
    if (lower.includes('/reports')) return 'Reports';
    if (lower.includes('/dashboard')) return 'Dashboard';
    return 'Page';
  }
}

